import {AfterViewInit, Component, ElementRef, Injectable, OnInit, ViewChild} from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {UserService} from '../service/user.service';
import {
  ARM_PHONE_NUMBER_PATTERN,
  EMAIL_PATTERN,
  PASSWORD_PATTERN,
  PHONE_NUMBER_PATTERN
} from '../constants/validation.constants';
import intlTelInput from 'intl-tel-input';
import {
  DEFAULT_INITIAL_COUNTRY,
  DEFAULT_ITI_POLITE,
  MAX_SEND_VERIFICATION_CODE_ATTEMPTS,
  MESSAGE_TIMEOUT
} from "../constants/global.constants";
import {UserCardUtil} from "../utilityClass/userCardUtil";
import {ActivatedRoute, Router} from "@angular/router";
import {HttpErrorResponse} from "@angular/common/http";
import {SharedConstants} from "../utilityClass/sharedConstants";
import {RegistrationUtlFactory} from "./registration-utl-factory.service";
import {InvitationTokenService} from "../service/invitation-token.service";
import {NavigationService} from "../service/navigation.service";


@Component({
  selector: 'app-registration',
  templateUrl: './registration.component.html',
  styleUrls: ['./registration.component.scss']
})
@Injectable({
  providedIn: 'root'
})
export class RegistrationComponent implements AfterViewInit, OnInit {

  private readonly ARMENIA_ISO = "374"
  private readonly ANY_LOWERCASE: RegExp = /\p{Ll}/u;
  private readonly ANY_UPPERCASE: RegExp = /\p{Lu}/u;

  registrationForm: FormGroup;
  errorMessage: string | null = null;
  successMessage: boolean = false;
  showPassword: boolean;
  invalidPhoneNumberMessage: string | null = null;
  showConfirmPassword: boolean;
  isRegistrationClicked: boolean = false;
  form: FormGroup | any
  iti: any;
  isButtonDisabled: boolean = true;
  isError: boolean = false;
  verificationErrorMessage: string = MAX_SEND_VERIFICATION_CODE_ATTEMPTS;
  reservedIdentificationId: string | null = null;
  returnUrl: string | null = null;
  criteriaList: string[] = ['minLength', 'maxLength', 'lowercase', 'uppercase'];

  @ViewChild('phoneInput') phone?: ElementRef<HTMLInputElement>;
  @ViewChild('confirmPasswordInput') confirmPasswordInput!: ElementRef;
  @ViewChild('passwordInput') passwordInput!: ElementRef;

  invitationToken: string | null = null;
  username: string | null = null;
  protected readonly SharedConstants = SharedConstants;

  constructor(
    private formBuilder: FormBuilder,
    private userService: UserService,
    private userCardUtil: UserCardUtil,
    private router: Router,
    private route: ActivatedRoute,
    private invitationTokenService: InvitationTokenService,
    private navigationService: NavigationService
  ) {
    this.isError = this.router.getCurrentNavigation()?.extras.state?.['isError'];
    this.registrationForm = this.createRegistrationForm();
    this.showPassword = false;
    this.showConfirmPassword = false;
    this.successMessage = false;
    this.handleFormChanges();
  }

  ngOnInit() {
    this.reservedIdentificationId = history.state.reservedIdentificationId || null;
    this.returnUrl = history.state.returnUrl || null;
    this.route.queryParams.subscribe(params => {
      this.invitationToken = params['token'];
      this.username = params['username'];
    })
  }

  ngAfterViewInit(): void {
    const phoneInput: HTMLInputElement | any = this.phone?.nativeElement;
    if (phoneInput) {
      this.iti = intlTelInput(phoneInput, {
        initialCountry: DEFAULT_INITIAL_COUNTRY,
        autoPlaceholder: DEFAULT_ITI_POLITE,
        separateDialCode: true,
        utilsScript: "node_modules/intl-tel-input/build/js/utils.js"
      } as intlTelInput.Options);
    }
    phoneInput.parentNode.style.display = 'block';
  }

  get countryIso() {
    if (this.iti?.getSelectedCountryData()) {
      return this.iti.getSelectedCountryData().dialCode;
    }
    return "";
  }

  createRegistrationForm(): FormGroup {
    return this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50), this.userCardUtil.whitespaceValidator()]],
      surname: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50), this.userCardUtil.whitespaceValidator()]],
      email: ['', [Validators.required, Validators.pattern(EMAIL_PATTERN)]],
      phoneNumber: ['', [Validators.required,Validators.pattern(PHONE_NUMBER_PATTERN)]],
      password: ['', [Validators.required, Validators.pattern(PASSWORD_PATTERN)]],
      confirmPassword: ['', Validators.required],
      privacyPolicyAccepted: [false, Validators.requiredTrue]
    }, {validator: this.passwordMatchValidator});
  }

  handleFormChanges() {
    this.registrationForm.valueChanges.subscribe({
      next: () => {
        this.isButtonDisabled = this.registrationForm.invalid || !this.isArmPhoneNumberValid();
      }
    })
  };

  onInputSpaceChange(event: Event, controlName: string): void {
    const input = event.target as HTMLInputElement;
    const cleanValueWithoutSpaces = input.value.replace(/\s/g, '');
    this.registerFormControl[controlName].setValue(cleanValueWithoutSpaces);
  }

  passwordMatchValidator(control: AbstractControl): { passwordMismatch: boolean } | null {
    const password = control.get('password')?.value;
    const confirmPassword = control.get('confirmPassword')?.value;
    return password !== confirmPassword ? {passwordMismatch: true} : null;
  }

  isPasswordValid(criteria: string): boolean {
    const password = this.registerFormControl['password'].value;

    switch (criteria) {
      case 'minLength':
        return password.length >= 8;
      case 'maxLength':
        return password.length <= 20;
      case 'lowercase':
        return this.ANY_LOWERCASE.test(password);
      case 'uppercase':
        return this.ANY_UPPERCASE.test(password);
      default:
        return false;
    }
  }

  preventDefaultIfNotNumber(event: KeyboardEvent) {
    const allowedChars = /[0-9]/;
    const inputChar = event.key;
    if (!allowedChars.test(inputChar) && inputChar != 'Backspace') {
      event.preventDefault();
    }
  }

  isArmPhoneNumberValid() {
    const phoneNumber = this.registerFormControl['phoneNumber'].value;
    if(this.countryIso == this.ARMENIA_ISO) {
      return ARM_PHONE_NUMBER_PATTERN.test(phoneNumber);
    }
    return true;
  }

  getCriteriaText(criteria: string): string {
    const criteriaText: { [key: string]: string } = {
      minLength: 'registration_content.min_characters',
      maxLength: 'registration_content.max_characters',
      lowercase: 'registration_content.lowercase',
      uppercase: 'registration_content.uppercase'
    };

    return criteriaText[criteria] || '';
  }

  register(): void {
    this.setInvitationTokenIfPresent();
    this.isRegistrationClicked = true;
    this.registrationForm.markAllAsTouched();
    if (this.registrationForm.valid) {
      if (this.iti.isValidNumber()) {
        const value = this.registrationForm.value;
        const countryISO = this.countryIso;
        value.countryIso = `${countryISO}`;
        value.phoneNumber = `${value.phoneNumber}`;
        value.token = this.invitationToken
        this.isButtonDisabled = true;
        value.email = this.lowercaseFirstLetter(value.email);
        value.username = this.username
        this.userService.register(value).subscribe({
            next: () => {
              this.isRegistrationClicked = false;
              this.successMessage = true;
              this.registrationForm.reset();
              setTimeout(() => {
                this.successMessage = false;
              }, MESSAGE_TIMEOUT);
              this.router.navigate(['/verify-phone'], {
                state: {
                  userPhoneNumber: value.phoneNumber,
                  countryISO: countryISO,
                  isNavigateToTeamPageAfterLogin: !!this.invitationToken,
                  reservedIdentificationId: this.reservedIdentificationId,
                  returnUrl: this.returnUrl
                }
              });
            },
            error: (error :HttpErrorResponse) => {
              this.isRegistrationClicked = false;
              this.handleError(error);
            }
          }
        );
      } else {
        this.isRegistrationClicked = false;
      }
    } else {
      this.isRegistrationClicked = false;
      this.errorMessage = '';
    }
  }

  goToLogin(): void {
    this.navigationService.navigateWithReservedIdAndReturnUrl('/login', this.reservedIdentificationId!, this.returnUrl!);
  }

  setInvitationTokenIfPresent(): void {
    if (this.invitationToken) {
      this.invitationTokenService.setInvitationToken(this.invitationToken);
    }
  }

  validatePhoneNumber(): void {
    const phoneNumberControl = this.registerFormControl['phoneNumber'];
    if (phoneNumberControl.valid) {
      this.invalidPhoneNumberMessage = '';
    } else {
      this.invalidPhoneNumberMessage = 'registration_content.valid_phone_number';
    }
  }

  togglePasswordVisibility(field: string): void {
    if (field === 'password') {
      this.showPassword = !this.showPassword;
      this.updateInputType(this.passwordInput.nativeElement, this.showPassword);
    } else if (field === 'confirmPassword') {
      this.showConfirmPassword = !this.showConfirmPassword;
      this.updateInputType(this.confirmPasswordInput.nativeElement, this.showConfirmPassword);
    }
  }

  private updateInputType(inputElement: HTMLInputElement, show: boolean): void {
    const fieldType = show ? 'text' : 'password';
    inputElement.type = fieldType;
  }

  private handleError(error: HttpErrorResponse): void {
    this.isButtonDisabled = false;
    if (error.status === 409) {
      const errorMessage = String(error.error.message);

      if(errorMessage.startsWith(RegistrationUtlFactory.ALREADY_EXISTS_ERRORS.EMAIL_AND_PHONE)) {
        this.errorMessage = RegistrationUtlFactory.ERROR_MESSAGES.EMAIL_ALREADY_EXISTS;
        this.invalidPhoneNumberMessage = RegistrationUtlFactory.ERROR_MESSAGES.PHONE_ALREADY_EXISTS;
      } else if (errorMessage.startsWith(RegistrationUtlFactory.ALREADY_EXISTS_ERRORS.EMAIL)) {
        this.errorMessage = RegistrationUtlFactory.ERROR_MESSAGES.EMAIL_ALREADY_EXISTS;
      } else if (errorMessage.startsWith(RegistrationUtlFactory.ALREADY_EXISTS_ERRORS.PHONE)) {
        this.invalidPhoneNumberMessage = RegistrationUtlFactory.ERROR_MESSAGES.PHONE_ALREADY_EXISTS;
      }
    } else {
      this.errorMessage = RegistrationUtlFactory.ERROR_MESSAGES.UNEXPECTED;
    }
  }


  resetErrorMessage(): void {
    this.errorMessage = '';
  }

  get registerFormControl() {
    return this.registrationForm.controls;
  }

  trimInputValue(event: Event, formControlName: string): void {
    const input = event.target as HTMLInputElement;
    const trimmedValue = input.value.trim();
    this.registrationForm.get(formControlName)?.setValue(trimmedValue, {emitEvent: false});
  }

  private lowercaseFirstLetter(str: string): string {
    return str.charAt(0).toLowerCase() + str.slice(1);
  }

}
